/**
 * URL query-param hygiene — pure, dependency-free.
 *
 * Scrivito performs no sanitization: junk values (`?q=`, `?q=undefined`, a bare
 * `?q`) survive parsing and are written back verbatim by `navigateTo`. This is
 * the single definition of what "junk" means, applied globally at the history
 * boundary (see `src/config/history.ts`) so malformed params from shared links,
 * hand-edited URLs, or buggy writers never reach the app or the URL bar.
 */

/**
 * Stringified values that are never legitimate user input — they only appear
 * when `String(undefined)` / `String(NaN)` leaks into a URL from a buggy writer.
 *
 * Deliberately excludes `'null'`: unlike `undefined`/`NaN`, the literal `null`
 * collides with real data — `Null` is a surname, and "null" is a searchable
 * term — so value-blacklisting it would strip legitimate params (the classic
 * "Christopher Null" false-positive). Empty/bare params are still stripped
 * structurally below; only the *value-content* heuristic omits `null`.
 */
const NULLISH_STRINGS = new Set(['undefined', 'NaN'])

/**
 * True if a single param value carries no information and should be stripped:
 * empty or whitespace-only (structural junk, always safe), or an exact
 * stringified `undefined`/`NaN` (a leaked value bug, never real input).
 */
export function isJunkParamValue(value: string): boolean {
  return value.trim() === '' || NULLISH_STRINGS.has(value)
}

/**
 * Strip junk values from a raw query string (leading `?` optional on input).
 *
 * Returns the cleaned search string (with a leading `?`, or `''` when nothing
 * remains), or `null` when the input was already clean — letting callers skip a
 * no-op navigation. Duplicate keys are preserved; only their junk values drop.
 */
export function sanitizeSearch(search: string): string | null {
  const cleaned = new URLSearchParams()
  let changed = false

  for (const [key, value] of new URLSearchParams(search)) {
    if (isJunkParamValue(value)) {
      changed = true
      continue
    }
    cleaned.append(key, value)
  }

  if (!changed) {
    return null
  }
  const result = cleaned.toString()
  return result ? `?${result}` : ''
}
